import { onlineOrderingProductImage } from '@trashlab/api/src/domains/pricing/shared/product-illustration';
import { Box, Flex, Text } from '@radix-ui/themes';
import StorageImage from '@trashlab/sitesapp/src/organizations/redebins/components/shared/storage-image';
import { trpcPricingListSkusOpen } from '@trashlab/api/src/generated/hooks';
import { useShoppingCart } from '@trashlab/sitesapp/src/organizations/redebins/hooks/use-shopping-cart';
import currency from 'currency.js';

export const RedebinsDumpsterCard = ({
  sku,
  onClose,
}: {
  sku: NonNullable<typeof trpcPricingListSkusOpen.output>[number];
  onClose?: () => void;
}) => {
  const containerPhoto = onlineOrderingProductImage(sku.products[0]);
  const { isLoading, addToCart } = useShoppingCart();

  const handleRentClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (sku.product?.id) {
      await addToCart({
        skuId: sku.id,
        productId: sku.product.id,
        onSuccess: onClose,
      });
    }
  };

  const cubicYardageRaw = sku.product?.cubicYardage;
  // Parse cubic yardage - handle both string ("10YD", "10") and number formats
  const cubicYardageNum =
    typeof cubicYardageRaw === 'string'
      ? parseInt(cubicYardageRaw.replace(/\D/g, ''), 10) || 0
      : Number(cubicYardageRaw) || 0;
  const cubicYardage = cubicYardageNum > 0 ? cubicYardageNum : null;

  // Specific features based on size - simplified title format for cards
  const dumpsterFeatures = cubicYardage === 10
    ? {
        title: '10YD',
        features: [
          'Fits in any standard parking space',
          'Holds about 10 cubic yards of debris',
          'Perfect for both commercial and residential projects',
          'Ideal for medium cleanouts and renovations',
        ],
        dimensions: '10x5x5',
      }
    : cubicYardage === 5
    ? {
        title: '5YD',
        features: [
          'Ideal for small cleanups and DIY projects',
          'Holds about 5 cubic yards of debris',
          'Built for heavier materials like shingles and concrete',
          'Perfect for tight spaces like alleyways',
        ],
        dimensions: '8x4x4',
      }
    : null;

  const size = dumpsterFeatures?.dimensions || '';
  const description =
    cubicYardage === 5
      ? 'Holds about 5 cubic yards of waste. Perfect for small house projects and heavier materials like shingles.'
      : cubicYardage === 10
      ? 'Holds about 10 cubic yards of waste. Excellent for medium-sized clean-ups & room renovations.'
      : sku.onlineOrderCopy || 'Perfect for cleanups & junk disposal';

  return (
    <Box className="w-full bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
      {/* Image */}
      {containerPhoto && (
        <Box className="relative p-8 bg-gray-50 flex items-center justify-center min-h-[200px]">
          <StorageImage
            alt="container image"
            bucket="container-product-images"
            fit="contain"
            logoPath={containerPhoto}
          />
        </Box>
      )}

      {/* Content */}
      <Flex direction="column" className="p-6">
        {/* Title */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {dumpsterFeatures?.title || (cubicYardage
            ? `${cubicYardage}YD`
            : 'Dumpster')}
        </h3>

        {/* Size Badge */}
        {size && (
          <span className="inline-block text-lg font-bold text-[#E31837] mb-3">
            {size}
          </span>
        )}

        {/* Features */}
        {dumpsterFeatures ? (
          <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
            {dumpsterFeatures.features.map((feature, idx) => (
              <li key={idx}>{feature}</li>
            ))}
          </ul>
        ) : (
          <>
            {/* Description for non-standard sizes */}
            <Text size="3" className="text-gray-600 mb-4">
              {description}
            </Text>
          </>
        )}

        {/* Price and CTA */}
        <Flex justify="between" align="center" className="mt-auto">
          <div>
            {sku.dumpsterPrice?.chargeFee && (
              <>
                <Text size="2" className="text-gray-500 uppercase block">
                  Starting at
                </Text>
                <Text size="5" weight="bold" className="text-gray-900">
                  {currency(sku.dumpsterPrice.chargeFee, {
                    fromCents: true,
                  }).format()}
                </Text>
              </>
            )}
          </div>

          <button
            onClick={handleRentClick}
            disabled={isLoading}
            className="bg-[#E31837] hover:bg-[#c41530] text-white font-bold py-3 px-6 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
          >
            {isLoading ? 'Loading...' : 'Book Now'}
          </button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default RedebinsDumpsterCard;
